import React, { useState, useEffect } from 'react';
import axios from 'axios';
import Swal from 'sweetalert2';
import 'bootstrap/dist/css/bootstrap.min.css';
import { useNavigate } from 'react-router-dom'; // Importing useNavigate
import Header from '../Components/Header';
import { BASE_URL } from '../Config';  

const SearchCustomerNo = () => {
  const [mobileNo, setMobileNo] = useState('');
  const [customerData, setCustomerData] = useState(null); // To store customer data after API response
  const [isLoading, setIsLoading] = useState(false); // To handle loading state
  const [saleRecordId, setSaleRecordId] = useState(null); // To store the hidden saleRecordId
  const [remarks, setRemarks] = useState(''); // To store the remarks input
  const [saleStatus, setSaleStatus] = useState(''); // To store the sale status
  const [superAgentId, setSuperAgentId] = useState(''); // State to store Super Agent ID
  const [isSubscribing, setIsSubscribing] = useState(false); // Loader for subscription button

  const navigate = useNavigate(); // Hook for navigation

  // This effect will run once on component mount to set the Super Agent ID from sessionStorage
  useEffect(() => {
    // Retrieve the user ID (which is the superAgentId) from sessionStorage
    const storedUserData = sessionStorage.getItem('user'); // Assuming the 'user' object is stored in sessionStorage
    if (storedUserData) {
      const userData = JSON.parse(storedUserData);
      setSuperAgentId(userData.userId); // Set userId as superAgentId
      console.log('Super Agent ID retrieved from sessionStorage:', userData.userId); // Debugging output
    } else {
      console.log('User data not found in sessionStorage. Please login again.');
    }
  }, []); // Only run once on mount

  const handleSearch = async (e) => {
    e.preventDefault();

    if (!mobileNo) {
      Swal.fire('Error', 'Please enter a mobile number.', 'error');
      return;
    }

    setIsLoading(true);

    try {
      const response = await axios.get(`${BASE_URL}Retail_GetCustomerInfo?MobileNo=${mobileNo}`, {
        headers: {
          'accept': '*/*',
        }
      });

      if (response.data.responseCode === "0000") {
        const customer = response.data.packages[0]; // Assuming only one package is returned
        setCustomerData(customer); // Storing customer data in state
        setSaleRecordId(customer.salerecordId); // Storing the saleRecordId in state (hidden)
      } else {
        Swal.fire('Error', response.data.message, 'error');
        setCustomerData(null); // Clear customer data on error
      }
    } catch (error) {
      console.error('Error fetching customer data:', error);
      Swal.fire('Error', 'An error occurred. Please try again later.', 'error');
      setCustomerData(null); // Clear customer data on error
    } finally {
      setIsLoading(false);
    }
  };

  const handlePackageSubscription = async () => {
    // First, check if the superAgentId is available
    if (!superAgentId) {
      Swal.fire('Error', 'Super Agent ID is missing. Please login again.', 'error');
      return;
    }

    // Ensure all required fields are filled
    if (!saleRecordId || !saleStatus || !remarks) {
      Swal.fire('Error', 'Please fill in all required fields.', 'error');
      return;
    }

    setIsSubscribing(true); // Set subscribing to true when the button is clicked

    try {
      const response = await axios.post(
        `${BASE_URL}Retail_PackageSubscription`,
            // 'http://124.109.46.74:8088/Retail_PackageSubscription',

        {
          srid: saleRecordId, // saleRecordId
          superagentID: superAgentId, // Use the superAgentId from sessionStorage
          saleStatus, // Sale status selected by the user
          remarks, // Remarks entered by the user
        },
        {
          headers: {
            'Content-Type': 'application/json',
            'accept': 'text/plain',
          }
        }
      );

      if (response.data.responseCode === "0000") {
        Swal.fire('Success', response.data.message, 'success').then(() => {
          // After showing the success message, navigate to the SearchCustomerNo page
          navigate('/SearchCustomerNo');  // Correct route with capital 'S' and 'C'
        });

        // Reset form state after successful subscription
        setMobileNo('');
        setCustomerData(null);
        setSaleRecordId(null);
        setRemarks('');
        setSaleStatus('');
      } else {
        Swal.fire('Error', response.data.message, 'error');
      }
    } catch (error) {
      console.error('Error subscribing to package:', error);
      Swal.fire('Error', 'An error occurred while subscribing to the package. Please try again later.', 'error');
    } finally {
      setIsSubscribing(false); // Reset subscribing state after request completes
    }
  };

  return (
    <>
      <Header />

      <div className="container-fluid d-flex justify-content-center align-items-center" style={{ minHeight: '50vh' }}>
        <div className="row justify-content-center w-100">
          <div className="col-12 col-sm-10 col-md-8 col-lg-6 col-xl-5 col-xxl-4 col-xxxl-3">
            <div className="card shadow-sm">
              <div className="card-body">
                <div className="text-center mb-4">
                  <h4>Search Customer Information</h4>
                </div>

                <form onSubmit={handleSearch}>
                  <div className="mb-3">
                    <label htmlFor="mobileNo" className="form-label">Mobile Number</label>
                    <input
                      type="text"
                      className="form-control"
                      id="mobileNo"
                      placeholder="Enter Customer Mobile Number"
                      value={mobileNo}
                      onChange={(e) => setMobileNo(e.target.value)}
                      required
                    />
                  </div>

                  <div className="d-grid gap-2 mt-4">
                    <button type="submit" className="btn btn-primary" disabled={isLoading}>
                      {isLoading ? 'Searching...' : 'Search Customer'}
                    </button>
                  </div>
                </form>

                {/* Show customer data if it exists */}
                {customerData && (
                  <div className="mt-4">
                    <h5>Customer Information</h5>
                    <div className="list-group">
                      <div className="list-group-item">
                        <strong>Customer Name:</strong> {customerData.customerName}
                      </div>
                      <div className="list-group-item">
                        <strong>Customer Number:</strong> {customerData.customerNumber}
                      </div>
                      <div className="list-group-item">
                        <strong>Package Name:</strong> {customerData.packageName}
                      </div>
                      <div className="list-group-item">
                        <strong>Package Price:</strong> Rs. {customerData.packagePrice}
                      </div>
                      <div className="list-group-item">
                        <strong>Package Duration:</strong> {customerData.packageDuration}
                      </div>
                    </div>

                    {/* Remarks Input */}
                    <div className="mb-3">
                      <label htmlFor="remarks" className="form-label">Remarks</label>
                      <textarea
                        className="form-control"
                        id="remarks"
                        rows="3"
                        placeholder="Enter remarks"
                        value={remarks}
                        onChange={(e) => setRemarks(e.target.value)}
                      />
                    </div>

                    {/* Sale Status Dropdown */}
                    <div className="mb-3">
                      <label htmlFor="saleStatus" className="form-label">Sale Status</label>
                      <select
                        className="form-select"
                        id="saleStatus"
                        value={saleStatus}
                        onChange={(e) => setSaleStatus(e.target.value)}
                      >
                        <option value="">Select Status</option>
                        <option value="Rejected">Rejected</option>
                        <option value="Completed">Completed</option>
                      </select>
                    </div>

                    {/* Subscribe Button */}
                    <div className="d-grid gap-2 mt-4">
                      <button
                        type="button"
                        className="btn btn-success"
                        onClick={handlePackageSubscription}
                        disabled={isSubscribing}
                      >
                        {isSubscribing ? 'Subscribing...' : 'Subscribe to Package'}
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default SearchCustomerNo;
